// v3 customer workbook: the sized use cases of a calculator state, rendered as a live-formula .xlsx.
//
// Input is the same `CalculatorInput` that `computeCalculator` takes (deal economics, the selected
// use cases with their answers / record levels / realization, the tool checklist). Output is a
// writer-agnostic WorkbookSpec; buildUseCaseWorkbook() turns it into bytes.
//
// Sheets
//   Summary     one row per included use case: gross value → realization → program value, plus the
//               pipeline created. Every figure is a formula over the Calculator and Inputs sheets.
//   Inputs      deal economics, then per use case its questions grouped by role (scaling / current /
//               with Clay) and its realization. The only sheet a customer needs to edit.
//   Calculator  one live-formula block per use case (one sub-block per record level) with the
//               sub-totals (records → opportunities → pipeline → revenue) and a gross value row.
//   Constants   the calendar constants the formulas use.   Legend   how to read the file.
//
// Kept OUT of the file on purpose, as in v2: the contract estimate, attribution and ROI. Formulas
// use direct cross-sheet references only and the workbook declares no defined names (they render
// `#NAME?` in Google Sheets and Numbers drops them). Every formula cell carries a cached value
// computed by evalExpr over the same expression so previewers that never recalculate show numbers.

import { DEFAULT_TOOLS16 } from '../data/tools';
import { getSizedUseCase, type QuestionRole, type SizedUseCase, type UseCaseQuestion } from '../data/use-case-catalog';
import type { QuestionKind } from '../types';
import { absRef, sheetAbsRef } from '../xlsx/ooxml';
import { buildXlsx } from '../xlsx/writer';
import type { Cell, NumFmt, SheetSpec, WorkbookSpec } from '../xlsx/writer';
import {
  DEFAULT_DEAL,
  computeCalculator,
  enabledLevels,
  enabledMethods,
  orderSelection,
  resolveUseCaseAnswers,
  toolRows,
  type CalculatorInput,
  type UseCaseSelection,
  type ToolRow,
} from './use-case-engine';
import { toExcel } from './xlsx-expr';
import type { ExcelEnv } from './xlsx-expr';
import {
  USE_CASE_CONSTANTS,
  evalSection,
  getUseCaseSpec,
  sectionsFor,
  specReadsDealKey,
  TOOL_CONSOLIDATION_ID,
  type SpecSection,
  type UseCaseSpec,
} from './xlsx-use-case-specs';

export interface UseCaseWorkbookInput {
  customerName: string;
  calculator: CalculatorInput;
  /** Injected clock — stamps the Summary and docProps. */
  generatedAt: Date;
  /**
   * A template for the customer to fill in later: the rep's typed answers, realization and deal
   * economics are ignored, so every input sits on Clay's default and is tagged as one to replace.
   * The selection, record levels and methods are kept (they decide which use cases and rows exist).
   */
  blank?: boolean;
}

/** The input a blank workbook is built from: selection shape only, everything else on Clay defaults. */
export function blankCalculatorOf(c: CalculatorInput): CalculatorInput {
  return {
    deal: { ...DEFAULT_DEAL },
    selected: c.selected.map((s) => ({ id: s.id, recordLevels: s.recordLevels, methods: s.methods })),
    tools: null,
    customTools: null,
  };
}

const BLANK_TAG = 'Clay default — replace with yours';

const SHEET_SUMMARY = 'Summary';
const SHEET_INPUTS = 'Inputs';
const SHEET_CALC = 'Calculator';
const SHEET_CONST = 'Constants';
const SHEET_LEGEND = 'Legend';

/** Value column on Inputs and Calculator (B). */
const V = 2;

const KIND_FMT: Record<QuestionKind, NumFmt> = {
  count: 'count',
  pct: 'pct',
  hrs: 'hrs',
  min: 'min',
  money: 'money',
  rate: 'num1',
};

const ROLE_ORDER: QuestionRole[] = ['scaling', 'current', 'with-clay'];
const ROLE_TITLE: Record<QuestionRole, string> = {
  scaling: 'Scaling — your volumes',
  current: 'Current — where you are today',
  'with-clay': 'With Clay — the improvement we are sizing',
};

const money = (v: number) => '$' + Math.round(v).toLocaleString('en-US');

function fmtDate(d: Date): string {
  return d.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
}

/** `clay-value-model-<customer-slug>-<yyyy-mm-dd>.xlsx` (`…-blank.xlsx` for the template). */
export function useCaseWorkbookFilename(customerName: string, dateStr: string, blank = false): string {
  const slug =
    customerName
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '')
      .slice(0, 40)
      .replace(/-+$/g, '') || 'customer';
  return `clay-value-model-${slug}-${dateStr}${blank ? '-blank' : ''}.xlsx`;
}

/** Provenance tag and fill for one input, the persisted form of the app's Assumed pills. */
export function workbookInputTag(role: QuestionRole, assumed: boolean, blank = false): { tag: string; style: Cell['s'] } {
  // The blank template: every input is an amber default to replace, with-Clay ones included.
  if (blank) return { tag: `[${role === 'with-clay' ? 'with Clay' : role === 'scaling' ? 'scaling' : 'current'} · ${BLANK_TAG}]`, style: 'inputAssumed' };
  if (role === 'with-clay') {
    return { tag: assumed ? '[with Clay · Clay assumption — validate in POC]' : '[with Clay · edited]', style: 'benchmark' };
  }
  const name = role === 'scaling' ? 'scaling' : 'current';
  return assumed
    ? { tag: `[${name} · assumed — Clay default, confirm]`, style: 'inputAssumed' }
    : { tag: `[${name} · yours]`, style: 'input' };
}

type Rows = Array<Array<Cell | null> | null>;
function putter(rows: Rows) {
  return (row: number, col: number, cell: Cell) => {
    const r = (rows[row - 1] ??= []);
    r[col - 1] = cell;
  };
}

/** Everything the Inputs sheet decided about one selected use case, for the other sheets. */
interface PlacedUseCase {
  uc: SizedUseCase;
  sel: UseCaseSelection;
  spec: UseCaseSpec;
  sections: SpecSection[];
  /** Resolved answers in engine units (whole percents), plus tool spends. */
  values: Record<string, number>;
  /** Inputs-sheet row of each input key (questions and tool rows). */
  inputRow: Record<string, number>;
  realizationRow: number;
  realizationPct: number;
  /** Calculator-sheet row of every spec row, by `${sectionIndex}:${key}`, then the gross row. */
  calcRow: Record<string, number>;
  grossRow: number;
  gross: number;
  /** Cached row values per section, by section index. */
  sectionValues: Array<Record<string, number>>;
  pipeline: number;
}

export function buildUseCaseWorkbookModel(input: UseCaseWorkbookInput): WorkbookSpec {
  const { generatedAt } = input;
  const blank = input.blank === true;
  const calculator = blank ? blankCalculatorOf(input.calculator) : input.calculator;
  const deal = calculator.deal;
  const customer = input.customerName.trim() || 'Customer';
  const selected = orderSelection(calculator.selected);
  const engine = computeCalculator(calculator);
  const toolsUntouched = calculator.tools == null && !(calculator.customTools ?? []).length;
  const tools: ToolRow[] = toolRows(calculator.tools ?? DEFAULT_TOOLS16, calculator.customTools);

  /* ---- Constants sheet (rows fixed up front: the formulas reference them) */
  const constKeys = Object.keys(USE_CASE_CONSTANTS);
  const constRow = (key: string) => 2 + constKeys.indexOf(key);

  /* ---- Inputs sheet */
  const inputs: Rows = [];
  const putIn = putter(inputs);
  putIn(1, 1, { v: `Inputs — ${customer}`, s: 'title' });
  putIn(2, 1, {
    v: blank
      ? 'Template: every cell is a Clay default (amber). Replace each with your own number and every sheet recalculates.'
      : 'Blue cells are inputs: edit them and every sheet recalculates. Amber cells are Clay defaults you have not yet confirmed.',
    s: 'note',
  });

  let r = 4;
  const bandRow = (title: string) => {
    putIn(r, 1, { v: title, s: 'header' });
    putIn(r, 2, { s: 'header' });
    putIn(r, 3, { s: 'header' });
    r += 1;
  };
  bandRow('Deal economics');
  const dealRow = { acv: r, win: r + 1, pipe: r + 2 };
  putIn(dealRow.acv, 1, { v: 'Average deal size (ACV)' });
  putIn(dealRow.acv, V, { v: deal.acv, s: blank ? 'inputAssumed' : 'input', nf: 'money' });
  putIn(dealRow.acv, 3, { v: blank ? `[scaling · ${BLANK_TAG}]` : '[scaling · yours]', s: 'note' });
  putIn(dealRow.win, 1, { v: 'Average win rate' });
  putIn(dealRow.win, V, { v: deal.winRate / 100, s: blank ? 'inputAssumed' : 'input', nf: 'pct' });
  putIn(dealRow.win, 3, { v: blank ? `[scaling · ${BLANK_TAG}]` : '[scaling · yours]', s: 'note' });
  r += 2;
  const needsPipeline = selected.some((s) => {
    const uc = getSizedUseCase(s.id);
    return uc ? specReadsDealKey(getUseCaseSpec(uc.id, tools.length), 'deal_current_pipeline') : false;
  });
  if (needsPipeline) {
    const given = Number(deal.currentAnnualPipeline) > 0;
    // The default is an Assumed $80M, so the template flags it like every other default.
    putIn(dealRow.pipe, 1, { v: 'Current annual pipeline' });
    putIn(dealRow.pipe, V, given ? { v: Number(deal.currentAnnualPipeline), s: blank ? 'inputAssumed' : 'input', nf: 'money' } : { s: 'inputAssumed', nf: 'money' });
    putIn(dealRow.pipe, 3, {
      v: blank
        ? `[scaling · ${BLANK_TAG}]${given ? '' : ' Required — Account / lead scoring shows $0 until it is filled in'}`
        : given
          ? '[scaling · yours]'
          : 'Required — Account / lead scoring reads it and shows $0 until it is filled in',
      s: 'note',
    });
    r += 1;
  }
  r += 1;

  const placed: PlacedUseCase[] = [];
  for (const sel of selected) {
    const uc = getSizedUseCase(sel.id) as SizedUseCase;
    const levels = enabledLevels(uc, sel.recordLevels);
    const methods = enabledMethods(uc, sel.methods);
    const spec = getUseCaseSpec(uc.id, tools.length);
    // Every method's questions are written (a method that is off just has its switch at 0), so the
    // customer can flip a method on in the file and the numbers are already there.
    const answers = resolveUseCaseAnswers(uc, sel.answers, levels, deal.acv, uc.methods.map((m) => m.id));
    const line = engine.rollup.lines.find((l) => l.id === uc.id);
    const realizationPct = line?.realizationPct ?? uc.realization.pct;

    const p: PlacedUseCase = {
      uc,
      sel,
      spec,
      sections: sectionsFor(spec, levels),
      values: { ...answers },
      inputRow: {},
      realizationRow: 0,
      realizationPct,
      calcRow: {},
      grossRow: 0,
      gross: 0,
      sectionValues: [],
      pipeline: 0,
    };

    bandRow(uc.name);
    const levelNote = levels.length ? ` · record levels: ${levels.join(' + ')}` : '';
    const methodNote = methods.length ? ` · methods: ${methods.map((m) => methodLetterOf(uc, m)).join(' + ')}` : '';
    putIn(r - 1, 3, { v: `${uc.tier} · ${uc.confidence.toLowerCase()}${levelNote}${methodNote}`, s: 'header' });

    if (uc.methods.length) {
      putIn(r, 1, { v: 'Time-savings methods — 1 = counted, 0 = left out (hours add, then the hourly rate applies once)', s: 'section' });
      putIn(r, 2, { s: 'section' });
      putIn(r, 3, { s: 'section' });
      r += 1;
      for (const m of uc.methods) {
        const key = spec.methodSwitches[m.id];
        p.values[key] = methods.includes(m.id) ? 1 : 0;
        p.inputRow[key] = r;
        putIn(r, 1, { v: `Method ${methodLetterOf(uc, m.id)}: ${m.label}` });
        putIn(r, V, { v: p.values[key], s: 'input', nf: 'count' });
        putIn(r, 3, { v: methods.includes(m.id) ? '[selected in the calculator]' : '[not selected in the calculator — set to 1 to count it]', s: 'note' });
        r += 1;
      }
      r += 1;
    }

    if (uc.id === TOOL_CONSOLIDATION_ID) {
      putIn(r, 1, { v: 'Displaced tools — annual contract value', s: 'section' });
      putIn(r, 2, { s: 'section' });
      putIn(r, 3, { s: 'section' });
      r += 1;
      tools.forEach((t, i) => {
        const key = `tool_${i + 1}`;
        p.values[key] = t.spend;
        p.inputRow[key] = r;
        putIn(r, 1, { v: t.name });
        putIn(r, V, { v: t.spend, s: toolsUntouched ? 'inputAssumed' : 'input', nf: 'money' });
        putIn(r, 3, { v: blank ? `[current · ${BLANK_TAG}]` : toolsUntouched ? '[current · assumed — Clay default contract, confirm]' : '[current · yours]', s: 'note' });
        r += 1;
      });
      if (!tools.length) {
        putIn(r, 1, { v: 'No displaced tools selected', s: 'note' });
        r += 1;
      }
    } else {
      const asked = uc.questions.filter((q) => !q.recordLevel || levels.includes(q.recordLevel));
      for (const role of ROLE_ORDER) {
        const qs = asked.filter((q) => q.role === role);
        if (!qs.length) continue;
        putIn(r, 1, { v: ROLE_TITLE[role], s: 'section' });
        putIn(r, 2, { s: 'section' });
        putIn(r, 3, { s: 'section' });
        r += 1;
        for (const q of qs) {
          p.inputRow[q.key] = r;
          placeQuestion(putIn, r, q, sel, p.values[q.key], q.method !== undefined && !methods.includes(q.method), blank);
          r += 1;
        }
      }
    }
    // Realization: blank in the app means "use the default", so the workbook always writes the
    // resolved number. The tag says whether the customer changed it.
    putIn(r, 1, { v: 'Realization', s: 'section' });
    putIn(r, 2, { s: 'section' });
    putIn(r, 3, { s: 'section' });
    r += 1;
    p.realizationRow = r;
    const rzAssumed = sel.realization === '' || sel.realization == null;
    putIn(r, 1, { v: 'Share of the modeled value you expect to realize' });
    putIn(r, V, { v: realizationPct / 100, s: rzAssumed ? 'inputAssumed' : 'input', nf: 'pct' });
    putIn(r, 3, {
      v: blank ? `[${BLANK_TAG}]: ${uc.realization.rationale}` : rzAssumed ? `[assumed — Clay default for ${uc.tier}, confirm]: ${uc.realization.rationale}` : '[yours]',
      s: 'note',
    });
    r += 2;
    placed.push(p);
  }

  /* ---- Calculator sheet */
  const calc: Rows = [];
  const putC = putter(calc);
  putC(1, 1, { v: `Calculator — ${customer}`, s: 'title' });
  putC(2, 1, {
    v: 'Every step is a live formula over the Inputs sheet. Gross value is before realization; the Summary applies it.',
    s: 'note',
  });
  let cr = 4;
  for (const p of placed) {
    putC(cr, 1, { v: p.uc.name, s: 'title' });
    cr += 1;
    putC(cr, 1, {
      v: `${p.uc.outcome === 'revenue' ? 'Revenue' : 'GTM cost saved'} · ${p.uc.path} path · ${p.uc.confidence.toLowerCase()}`,
      s: 'note',
    });
    cr += 2;

    const sectionTotals: string[] = [];
    p.sections.forEach((section, si) => {
      // Where each key lives, for the Excel renderer. Rows of this section are filled in as placed.
      const cellOf: Record<string, string> = {};
      for (const k of Object.keys(p.inputRow)) cellOf[k] = sheetAbsRef(SHEET_INPUTS, p.inputRow[k], V);
      const env: ExcelEnv = {
        cell: (key) => {
          if (key in cellOf) return cellOf[key];
          if (key === 'deal_acv') return sheetAbsRef(SHEET_INPUTS, dealRow.acv, V);
          if (key === 'deal_win_rate') return sheetAbsRef(SHEET_INPUTS, dealRow.win, V);
          if (key === 'deal_current_pipeline') return sheetAbsRef(SHEET_INPUTS, dealRow.pipe, V);
          if (key in USE_CASE_CONSTANTS) return sheetAbsRef(SHEET_CONST, constRow(key), V);
          throw new Error(`${p.uc.id}: no cell for ${key}`);
        },
        timeToggleCell: '',
      };
      const base: Record<string, number> = {
        ...p.values,
        deal_acv: deal.acv,
        deal_win_rate: deal.winRate,
        deal_current_pipeline: Number(deal.currentAnnualPipeline) > 0 ? Number(deal.currentAnnualPipeline) : 0,
      };
      const values = evalSection(section, base);
      p.sectionValues[si] = values;

      if (section.level) {
        putC(cr, 1, { v: section.title, s: 'section' });
        putC(cr, 2, { s: 'section' });
        putC(cr, 3, { s: 'section' });
        cr += 1;
      }
      for (const row of section.rows) {
        cellOf[row.key] = absRef(cr, V);
        p.calcRow[`${si}:${row.key}`] = cr;
        const isValue = row.key === section.valueKey;
        putC(cr, 1, { v: row.label });
        putC(cr, V, {
          f: toExcel(row.expr, env),
          v: values[row.key],
          s: isValue ? 'sectionOutput' : 'intermediate',
          nf: row.fmt,
        });
        if (row.sub === 'pipeline') p.pipeline += values[row.key];
        cr += 1;
      }
      sectionTotals.push(absRef(p.calcRow[`${si}:${section.valueKey}`], V));
      p.gross += values[section.valueKey];
    });

    p.grossRow = cr;
    putC(cr, 1, { v: 'Gross annual value (before realization)', s: 'total' });
    putC(cr, V, { f: sectionTotals.join('+') || '0', v: p.gross, s: 'total', nf: 'money' });
    if (p.sections.length > 1) putC(cr, 3, { v: 'Record levels add; lower realization if they overlap', s: 'note' });
    cr += 3;
  }

  /* ---- Summary sheet */
  const summary: Rows = [];
  const putS = putter(summary);
  putS(1, 1, { v: `Clay value model — ${customer}`, s: 'title' });
  putS(2, 1, {
    v: `Generated ${fmtDate(generatedAt)} from the Clay value calculator. Edit the blue cells on the Inputs sheet and every total recalculates; the Calculator sheet shows every step.`,
    s: 'note',
  });
  putS(3, 1, {
    v: 'Value estimate — every figure is modeled from the inputs and will vary for your business; it is not a guarantee of results.',
    s: 'note',
  });
  const headers = ['Use case', 'Gross value', 'Realization %', 'Program value', 'Pipeline created'];
  headers.forEach((h, i) => putS(5, i + 1, { v: h, s: 'header' }));
  const ROW0 = 6;
  let sr = ROW0;
  for (const p of placed) {
    const gross = sheetAbsRef(SHEET_CALC, p.grossRow, V);
    const rz = absRef(sr, 3);
    const rzValue = p.realizationPct / 100;
    putS(sr, 1, { v: p.uc.name });
    putS(sr, 2, { f: gross, v: p.gross, nf: 'money' });
    putS(sr, 3, { f: sheetAbsRef(SHEET_INPUTS, p.realizationRow, V), v: rzValue, nf: 'pct' });
    putS(sr, 4, { f: `${absRef(sr, 2)}*${rz}`, v: p.gross * rzValue, nf: 'money' });
    const pipeRefs = p.sections.flatMap((section, si) =>
      section.rows.filter((x) => x.sub === 'pipeline').map((x) => sheetAbsRef(SHEET_CALC, p.calcRow[`${si}:${x.key}`], V)),
    );
    if (p.uc.path === 'pipeline' && pipeRefs.length) {
      putS(sr, 5, { f: `(${pipeRefs.join('+')})*${rz}`, v: p.pipeline * rzValue, nf: 'money' });
    }
    sr += 1;
  }
  const last = sr - 1;
  const rangeOf = (col: number) => (placed.length ? `${absRef(ROW0, col)}:${absRef(last, col)}` : null);
  const sumOf = (col: number) => (rangeOf(col) ? `SUM(${rangeOf(col)})` : '0');
  sr += 1;
  const programTotal = placed.reduce((t, p) => t + p.gross * (p.realizationPct / 100), 0);
  const grossTotal = placed.reduce((t, p) => t + p.gross, 0);
  const pipelineTotal = placed.reduce((t, p) => t + (p.uc.path === 'pipeline' ? p.pipeline * (p.realizationPct / 100) : 0), 0);
  putS(sr, 1, { v: 'Use cases included', s: 'intermediate' });
  putS(sr, 2, { f: rangeOf(1) ? `COUNTA(${rangeOf(1)})` : '0', v: placed.length, nf: 'count', s: 'intermediate' });
  sr += 1;
  putS(sr, 1, { v: 'Total gross value', s: 'intermediate' });
  putS(sr, 2, { f: sumOf(2), v: grossTotal, nf: 'money', s: 'intermediate' });
  sr += 1;
  putS(sr, 1, { v: 'Total program value', s: 'total' });
  putS(sr, 2, { f: sumOf(4), v: programTotal, nf: 'money', s: 'total' });
  putS(sr, 3, { v: 'Gross value × realization — what the story leads with', s: 'note' });
  sr += 1;
  putS(sr, 1, { v: 'Pipeline created (program basis)', s: 'intermediate' });
  putS(sr, 2, { f: sumOf(5), v: pipelineTotal, nf: 'money', s: 'intermediate' });
  sr += 1;
  if (needsPipeline && !(Number(deal.currentAnnualPipeline) > 0)) {
    sr += 1;
    putS(sr, 1, { v: 'Account / lead scoring shows $0 until the current annual pipeline is entered on the Inputs sheet.', s: 'note' });
  }
  if (tools.length) {
    sr += 1;
    putS(sr, 1, { v: `Tool consolidation counts ${tools.length} displaced tool contract${tools.length === 1 ? '' : 's'} (${money(tools.reduce((t, x) => t + x.spend, 0))} a year).`, s: 'note' });
  }

  /* ---- Constants */
  const constRows: Array<Array<Cell | null>> = [[{ v: 'Constant', s: 'header' }, { v: 'Value', s: 'header' }, { v: 'Used for', s: 'header' }]];
  for (const k of constKeys) {
    const c = USE_CASE_CONSTANTS[k];
    constRows.push([{ v: c.label }, { v: c.v, s: 'intermediate', nf: 'count' }, { v: c.note, s: 'note' }]);
  }

  /* ---- Legend */
  const legend: Array<[string, string, string]> = [
    ['Workbook', 'Provenance', `Generated ${fmtDate(generatedAt)} by the Clay value calculator for ${customer}. The arithmetic mirrors the calculator's engine step for step; only the use cases included in the conversation are here.`],
    ['Workbook', 'Sheets', 'Summary (one row per use case, totals) · Inputs (every editable number) · Calculator (one formula block per use case) · Constants · Legend.'],
    ['Model', 'Gross value', 'Revenue (pipeline × win rate) or GTM cost saved, before realization. A change from today to with Clay is never negative: Clay is not modeled as removing value.'],
    ['Model', 'Realization %', 'The share of the modeled value you expect to realize. Blank is not zero in the calculator, so leave the default rather than clearing it.'],
    ['Model', 'Program value', 'Gross value × realization. This is the headline figure.'],
    ['Model', 'Time-savings methods', 'GTM automation can size the hours Clay saves three ways: work hours × automation share, a flat hours-per-week figure, or minutes saved per record × records worked. Each has a 1 / 0 switch on the Inputs sheet; the hours of every method switched on add, then the hourly rate applies once.'],
    ['Model', 'Record levels', 'CRM enrichment and TAM sourcing can be sized on accounts, contacts or both. When both are on their values add; lower realization if the records overlap.'],
    ['Inputs', '[scaling]', 'Your volumes: records, headcount, spend, rates that scale the result.'],
    ['Inputs', '[current]', 'Where you are today: fill rate, match rate, output per rep.'],
    ['Inputs', '[with Clay]', 'The improvement being sized, from Clay benchmarks. Validate in a POC.'],
    ['Inputs', 'yours / assumed', 'A tag ending in "yours" is a number you gave. "assumed — Clay default" means the calculator default is still standing: the first thing to pressure-test.'],
    ['Cell color', 'Light blue fill, blue text', 'Customer input — edit these.'],
    ['Cell color', 'Light amber fill, blue text', 'A Clay default you have not yet confirmed.'],
    ['Cell color', 'Blue italic, no fill', 'Clay benchmark / POC input.'],
    ['Cell color', 'Bold black', 'Intermediate calculation.'],
    ['Cell color', 'Light green fill', 'A use case section’s gross value.'],
    ['Cell color', 'Dark green fill, white', 'A use case total, or the program total.'],
    ['Caveats', 'Value estimate', 'Every figure in this workbook is a modeled estimate built from the inputs on the Inputs sheet, not a guarantee of results. Change an input and the totals change with it.'],
  ];
  const legendRows: Array<Array<Cell | null>> = [[{ v: 'Section', s: 'header' }, { v: 'Item', s: 'header' }, { v: 'Definition', s: 'header' }]];
  legend.forEach(([a, b, c]) => legendRows.push([{ v: a, s: 'intermediate' }, { v: b }, { v: c, wrap: true }]));

  const sheets: SheetSpec[] = [
    { name: SHEET_SUMMARY, rows: summary, cols: [48, 18, 16, 18, 18] },
    { name: SHEET_INPUTS, rows: inputs, cols: [58, 18, 70] },
    { name: SHEET_CALC, rows: calc, cols: [70, 20, 48] },
    { name: SHEET_CONST, rows: constRows, cols: [30, 12, 70] },
    { name: SHEET_LEGEND, rows: legendRows, cols: [14, 30, 110] },
  ];
  return { title: `Clay value model — ${customer}`, sheets };
}

function placeQuestion(
  put: (row: number, col: number, cell: Cell) => void,
  row: number,
  q: UseCaseQuestion,
  sel: UseCaseSelection,
  resolved: number,
  methodOff = false,
  blank = false,
): void {
  const saved = (sel.answers ?? {})[q.key];
  const assumed = saved === undefined;
  const { tag, style } = workbookInputTag(q.role, assumed, blank);
  const nf = KIND_FMT[q.kind];
  put(row, 1, { v: q.label });
  // A cleared answer is written blank (it evaluates as 0), not as a literal zero.
  put(row, V, saved === '' ? { s: style, nf } : { v: q.kind === 'pct' ? resolved / 100 : resolved, s: style, nf });
  put(row, 3, { v: methodOff ? `${tag} · its method is off (switch above)` : tag, s: 'note' });
}

const methodLetterOf = (uc: SizedUseCase, id: string): string =>
  String.fromCharCode(65 + Math.max(0, uc.methods.findIndex((m) => m.id === id)));

/** The finished file. */
export function buildUseCaseWorkbook(input: UseCaseWorkbookInput): Uint8Array<ArrayBuffer> {
  return buildXlsx(buildUseCaseWorkbookModel(input), { now: input.generatedAt });
}
